import './MortgageStupidLink.css';

import React from 'react';
import { Typography } from '@vtblife/uikit';

import { ApplicationIdEnum } from '@search/graphql-typings';
import classname from '@search/classname/src';

import { ClassifiedAnalytics } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedAnalytics';

import { formatLocaleNumber } from '@search/helpers/src/formatNumber';
import { useClassifiedOfferDataLayerPush } from '../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { defaultMortgageRateNbFormatted, defaultMortgageRateSecondFormatted } from '../contants';
import { useMortgagePage } from '../useMortgagePage';
import { MortgageStupidModal } from './MortgageStupidModal';
import type { MortgageStupidProps } from './useStupidMortgage';

const cn = classname.bind(null, 'MortgageDumbLink');

export function MortgageStupidLink(
    {
        className,
        regionId,
        realtyType,
        isSnippetLink,
        isNewBuilding,
        gtmSendId,
        linkGtmId = ClassifiedAnalytics.IpotekaFromOffer,
        locationPlacement,
        offerUrl,
        onClick,
        scrollToMortgageCalculator,
        propertyCost,
        minimalMortgageRate
    }: {
        linkGtmId?: string;
        isSnippetLink?: boolean;
        className?: string;
        regionId: number;
        onClick?: () => void;
        scrollToMortgageCalculator?: () => void;
        propertyCost: number;
    } & MortgageStupidProps
) {
    const [ isShowMortgageStarter, setShowMortgageStarter ] = React.useState(false);
    const { openLink } = useMortgagePage(false);
    const onClose = React.useCallback(() => setShowMortgageStarter(false), [ ]);
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const onLinkClick = React.useCallback((e?: MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (linkGtmId) dataLayerPush({ event: linkGtmId });
        setShowMortgageStarter(true);
        onClick?.();
    }, [ linkGtmId, dataLayerPush, onClick, scrollToMortgageCalculator ]);

    const linkTitle = React.useMemo(() => {
        if (! isNewBuilding) return `Ипотека на\u00A0вторичку от\u00A0${formatLocaleNumber(minimalMortgageRate) || defaultMortgageRateSecondFormatted}%`;

        return isSnippetLink ? `Ипотека с\u00A0господдержкой от\u00A0${formatLocaleNumber(minimalMortgageRate) || defaultMortgageRateNbFormatted}%` : 'Рассчитать ипотеку';
    }, [ isNewBuilding, isSnippetLink, minimalMortgageRate ]);

    const linkSubTitle = React.useMemo(() => {
        if (! isNewBuilding) return 'Одна заявка во все банки';

        return 'Оформите «Семейную» или «Льготную ипотеку» по\u00A0выгодной ставке';
    }, [ isNewBuilding ]);

    return (<div className={cn(null, { isSnippetLink }, className)}>
        {isShowMortgageStarter ? <MortgageStupidModal
            onClose={onClose}
            gtmSendId={gtmSendId}
            regionId={regionId}
            propertyCost={propertyCost}
            locationPlacement={locationPlacement}
            offerUrl={offerUrl}
            isNewBuilding={isNewBuilding}
            applicationId={ApplicationIdEnum.ClassifiedDesktop}
            realtyType={realtyType}
            minimalMortgageRate={minimalMortgageRate}
        /> : null}
        <Typography
            onClick={openLink}
            data-test='snippet-mortgage-link'
            className={cn('link', { isBottomPosition: ! isSnippetLink })}
            color='blue-500'
            bold
        >
            {linkTitle}
        </Typography>
        <Typography
            variant='secondary-alone'
            className={cn('flex')}
            data-test='snippet-mortgage-description'
        >
            {linkSubTitle}
        </Typography>
    </div>);
}
